import type { TFunction } from "i18next";
import type { JSX, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { holatVar, KpiTile, type KpiTileDelta } from "@/shared/charts";
import type { ReportAnalytics, ReportKind } from "../../types";

const SON_FORMATI = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 });

/** Minus belgisi — defis emas, raqam bilan bir kenglikda. */
const MINUS = "−";

/**
 * Son → matn: minglik ajratkich bo'linmas bo'shliq, o'nlik VERGUL
 * («4 218», «92,4»), manfiyda `−` — `foizMatni` bilan bir xil ko'rinish.
 *
 * ⚠️ Hisobotlar modulidagi YAGONA son formatlovchisi (hub, analitika,
 * davomat layoutlari shu yerdan oladi). Butun songacha yumaloqlash
 * kerak bo'lsa, chaqiruv joyida `Math.round` qiling.
 */
export function sonMatni(qiymat: number): string {
	return SON_FORMATI.format(qiymat).replace("-", MINUS);
}

/**
 * Foiz ko'rsatkichi kalitlari — delta nisbiy foiz EMAS, «p.p.» bo'lib
 * hisoblanadi (foizning foizi o'qib bo'lmaydigan son beradi).
 */
const FOIZ_KALITLARI = new Set(["rate"]);

function standartFormat(key: string, qiymat: number): string {
	return FOIZ_KALITLARI.has(key) ? `${sonMatni(qiymat)}%` : sonMatni(qiymat);
}

/**
 * Oldingi oyna bilan solishtirish (spec §2.3 `previous`).
 *
 * ⚠️ `previous === null` → delta YO'Q (oyna hisoblanmadi); `0` →
 * nisbiy foiz ma'nosiz (nolga bo'lish), shuning uchun MUTLAQ farq
 * yoziladi. Yo'nalish glif bilan ham beriladi (`KpiTile`).
 */
export function kpiDelta(
	key: string,
	value: number,
	previous: number | null,
	format: (v: number) => string,
	t: TFunction
): KpiTileDelta | undefined {
	if (previous === null) {
		return undefined;
	}

	const farq = Math.round((value - previous) * 10) / 10;
	const direction = farq > 0 ? "up" : farq < 0 ? "down" : "flat";
	const ishora = farq > 0 ? "+" : farq < 0 ? MINUS : "";
	const mutlaq = Math.abs(farq);
	let text: string;

	if (FOIZ_KALITLARI.has(key)) {
		text = `${ishora}${sonMatni(mutlaq)} ${t("kpi.ppSuffix")}`;
	} else if (previous === 0) {
		text = `${ishora}${format(mutlaq)}`;
	} else {
		text = `${ishora}${sonMatni(Math.round((mutlaq / Math.abs(previous)) * 1000) / 10)}%`;
	}

	return { text, direction, label: t("analytics.vsPrevious") };
}

/** `items` rejimidagi bitta karta. */
export interface KpiItem {
	/** `totals[].key` — yorliq standart holda `analytics.totals.<key>`. */
	key: string;
	/** Berilsa yorliq shu matn (tarjima qilingan). */
	label?: string;
	/** Qiymat formatlovchisi; `format` xaritasidagidan ustun turadi. */
	format?: (v: number) => string;
	danger?: boolean;
	/** Kartaning pastki qismi (masalan bullet yoki segment bar). */
	hint?: ReactNode;
	/** `true` — `spark` bo'lsa ham sparkline chizilmaydi (hint o'rnini egallaydi). */
	noSpark?: boolean;
}

export interface KpiRowProps {
	kind: ReportKind;
	totals: ReportAnalytics["totals"];
	spark: ReportAnalytics["spark"];
	/** Kalit bo'yicha qiymat formatlovchisi (masalan daqiqa → `H:MM`). */
	format?: Partial<Record<string, (v: number) => string>>;
	/** Qizil (`tone="danger"`) chiziladigan kalitlar — ruxsatsiz, kelmagan… */
	danger?: string[];
	/**
	 * Berilsa kartalar SHU tartib va to'plamda chiziladi (`totals` da
	 * yo'q kalit `0`, deltasiz). Berilmasa `totals` ning har kaliti.
	 */
	items?: KpiItem[];
}

/**
 * KPI qatori — `totals` ning HAR kaliti uchun bitta `KpiTile`.
 *
 * ⚠️ Yorliq `analytics.totals.<key>` dan (spec §2.4: kalit — i18n
 * kaliti ham); kalit yo'q bo'lsa xom kalit ko'rinmasin deb kalitning
 * o'zi `defaultValue` bo'ladi.
 *
 * ⚠️ Sparkline `spark` dan KALIT bo'yicha topiladi, indeks bo'yicha
 * EMAS — backend tartibi bir xil bo'lishi kafolatlanmagan.
 */
export function KpiRow({ kind, totals, spark, format, danger, items }: KpiRowProps): JSX.Element {
	const { t } = useTranslation("reports");
	const kartalar: KpiItem[] = items ?? totals.map((total) => ({ key: total.key }));

	return (
		<div
			data-testid="kpi-row"
			data-kind={kind}
			className="grid gap-3"
			style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}
		>
			{kartalar.map((item) => {
				const { key } = item;
				const total = totals.find((x) => x.key === key);
				const qiymat = total?.value ?? 0;
				const xavfli = item.danger ?? danger?.includes(key) ?? false;
				const formatlovchi =
					item.format ?? format?.[key] ?? ((v: number) => standartFormat(key, v));
				const nuqtalar = item.noSpark ? undefined : spark.find((s) => s.key === key)?.points;

				return (
					<div key={key} data-kpi-key={key} className="min-w-0">
						<KpiTile
							label={item.label ?? t(`analytics.totals.${key}`, { defaultValue: key })}
							value={formatlovchi(qiymat)}
							tone={xavfli ? "danger" : "default"}
							hint={item.hint}
							delta={kpiDelta(key, qiymat, total?.previous ?? null, formatlovchi, t)}
							spark={
								nuqtalar && nuqtalar.length > 1
									? {
											points: nuqtalar,
											color: holatVar(xavfli ? "kelmagan" : "kelgan"),
										}
									: undefined
							}
						/>
					</div>
				);
			})}
		</div>
	);
}
